import { copyToClipboard } from '@/lib/clipboard.js';
import { notify } from '@/lib/notify.js';
import type { WorkspaceArtifact } from '@/lib/api/workspace-artifacts.js';

import { type ArtifactViewer, canManageArtifact } from './artifact-display.js';
import { artifactAudienceSentence } from './artifact-sharing.js';
import {
  sharePointConnectionSearchFor,
  sharePointSearchFor,
  type SharePointTreePosition,
} from './artifacts-search.js';

/**
 * The links a colleague can be sent — to a workspace file, or to a place in the workspace's SharePoint
 * tree — and what has to be said out loud when the file is not open to the whole workspace.
 *
 * Both links point into THIS interface. The platform hands the browser no presigned URL and no storage
 * address, and a SharePoint `web_url` is a convenience rather than a way in (the tree is read on the
 * workspace's shared connection, so the recipient's own SharePoint rights say nothing about it). So the
 * only address worth sharing is the section that renders the file, opened under the recipient's own
 * session and checked against their workspace membership the ordinary way.
 */

/** The section both links land on. */
const ARTIFACTS_PATH = '/artifacts';

/**
 * The link to one workspace file. The `file` parameter is the section's existing deep link — the same one
 * the knowledge shelf uses — so a recipient lands on the file's preview.
 *
 * `origin` is the origin of the document doing the copying, never a configured host: the interface is
 * reached on a different origin per environment, and a link built from anything else would send a
 * colleague to the wrong one.
 */
export function artifactLink(
  origin: string,
  artifactId: string,
  tab: 'files' | 'archived' = 'files',
): string {
  const orderedSearch = new URLSearchParams();
  if (tab === 'archived') orderedSearch.set('tab', 'archived');
  orderedSearch.set('file', artifactId);
  return `${origin}${ARTIFACTS_PATH}?${orderedSearch.toString()}`;
}

/**
 * The link to a place in the SharePoint tree — which SharePoint, and where inside it. Both are written
 * through the same mappings the route navigates with ({@link sharePointConnectionSearchFor},
 * {@link sharePointSearchFor}), so the link and the router can never disagree about what a parameter is
 * called. Anything that is not addressed is left out entirely, because a blank id in the URL normalizes
 * to no id at all.
 *
 * The connection is not optional, and a `null` for it is a claim rather than a default: it says the
 * sender was browsing whichever connection the service resolves. Dropping it silently would send the
 * recipient to the same Graph ids in a DIFFERENT SharePoint, which is a different document under the
 * expected name.
 */
export function sharePointLink(
  origin: string,
  position: SharePointTreePosition,
  connectionId: string | null,
): string {
  const search = new URLSearchParams({ tab: 'sharepoint' });
  const parameters = {
    ...sharePointConnectionSearchFor(connectionId),
    ...sharePointSearchFor(position),
  };
  for (const [key, value] of Object.entries(parameters)) {
    if (value !== undefined) search.set(key, value);
  }
  return `${origin}${ARTIFACTS_PATH}?${search.toString()}`;
}

/**
 * What copying a narrow file's link has to warn about: the link may land on somebody who is not in the
 * file's audience, and following it will tell them the file does not exist rather than show it.
 *
 * It names the audience and stops there. It used to say the recipient "won't be able to open it", which
 * was true while a file was either private or open to everyone — and became false the moment a file
 * could be shared with a chat or with a picked list, because those are exactly the people who CAN open
 * it. Whether one particular recipient is inside the audience is the server's question, not this
 * screen's, so the warning states what is true of the file and leaves the person out of it.
 *
 * The way out depends on who is copying. Only the file's author holding editor rights, or a workspace
 * owner inside its audience, may widen its visibility (the service's `canChangeArtifactVisibility`,
 * mirrored by {@link canManageArtifact}), so only they are pointed at Share…. Everyone who is not the
 * author is told to ask the author. The author who holds the viewer role is told what they lack, because
 * telling them to ask the author would send them to themselves. An author whose role is unresolved or
 * unknown to this build gets the audience alone: neither way out can be promised truthfully.
 */
export function restrictedArtifactWarning(
  artifact: Pick<WorkspaceArtifact, 'visibility' | 'createdBy'>,
  viewer: ArtifactViewer,
): string {
  const audience =
    artifactAudienceSentence(artifact) ?? 'Only you can open this file.';
  if (canManageArtifact(artifact, viewer)) {
    return `${audience} Open it up from the file's ⋮ menu → Share… if they should see it.`;
  }
  if (viewer.userId === null || viewer.userId !== artifact.createdBy) {
    return `${audience} Ask its author to widen it.`;
  }
  return viewer.role === 'viewer'
    ? `${audience} You need editor access in this workspace to widen it.`
    : audience;
}

/**
 * Put one file's link on the clipboard from wherever the file is on screen — the section's tile, its table
 * row, its ⋮ menu, or the chat's file panel. One function so all four copy the same address and warn in
 * the same words.
 *
 * The link is built from the CURRENT document's origin, so it points back at the environment the user is
 * actually in. A private file adds the warning, and only once the copy has succeeded: if nothing reached
 * the clipboard there is no link on its way to anyone, and a warning about it would be noise on top of
 * the failure the user was already told about.
 *
 * `visibility` is an open union, so anything that is not literally `workspace` is treated as narrower
 * than the workspace — the same fail-closed reading the display predicates use.
 */
export async function copyArtifactLink(
  artifact: Pick<
    WorkspaceArtifact,
    'id' | 'visibility' | 'createdBy' | 'status'
  >,
  viewer: ArtifactViewer,
): Promise<void> {
  const copied = await copyToClipboard(
    artifactLink(
      window.location.origin,
      artifact.id,
      artifact.status === 'archived' ? 'archived' : 'files',
    ),
    'File link copied',
  );
  if (copied && artifact.visibility !== 'workspace') {
    notify.warning(restrictedArtifactWarning(artifact, viewer));
  }
}
